import React, { useState, useRef, useEffect } from "react";
import { MoreVertical, ChevronLeft, ChevronRight, ChevronUp, MoreHorizontal} from "lucide-react";

import Checkbox from "../../../components/Checkbox/Checkbox";
import teamBadgePlaceholder from "../../../assets/images/team_badge_placeholder.svg";
import { getStatusStyle, STATUS_LABELS, TAB_TYPES, ROWS_PER_PAGE_OPTIONS } from "../constants";

/**
 * Participants Table Component
 */
const ParticipantsTable = ({
  data,
  activeTab,
  selectedRows,
  onSelectAll,
  onSelectRow,
  onViewTeam,
  onOpenActionMenu,
  onOpenDeleteModal,
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  showCategoryColumn,
}) => {
  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  const rowsDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rowsDropdownRef.current && !rowsDropdownRef.current.contains(event.target)) {
        setShowRowsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
    <div className="w-full overflow-x-auto rounded-2xl border border-app-hover">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-app-surface border-b border-app-hover">
            <th className="p-4 w-12">
              <Checkbox
                checked={selectedRows.length === data.length && data.length > 0}
                onChange={onSelectAll}
              />
            </th>
            <th className="p-4 text-sm font-medium text-app-text-secondary">Name</th>
            <th className="hidden sm:table-cell p-4 text-sm font-medium text-app-text-secondary">
              {activeTab === TAB_TYPES.TEAMS ? "Coach" : "Email"}
            </th>
            {showCategoryColumn && (
              <th className="p-4 text-sm font-medium text-app-text-secondary">Category</th>
            )}
            <th className="p-4 text-sm font-medium text-app-text-secondary">Status</th>
            <th className="p-4 w-12" />
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <TableRow
              key={item.id}
              item={item}
              activeTab={activeTab}
              isSelected={selectedRows.includes(item.id)}
              showCategoryColumn={showCategoryColumn}
              onSelect={() => onSelectRow(item.id)}
              onViewTeam={() => onViewTeam(item.id)}
              onOpenActionMenu={onOpenActionMenu}
              onOpenDeleteModal={() => onOpenDeleteModal(item)}
            />
          ))}
        </tbody>
      </table>
    </div>

    {/* Pagination */}
    <div className="flex flex-col sm:flex-row items-center justify-between gap-2 py-3">
      {/* Left: Prev / Next */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-app-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={18} className="text-clubbie-white" />
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-app-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={18} className="text-clubbie-white" />
        </button>
      </div>

      {/* Center: Show X-Y of Z */}
      <span className="text-sm text-app-text-muted">
        Show {startIndex + 1}-{endIndex} of {totalItems} {activeTab}
      </span>

      {/* Right: Rows per page */}
      <div className="hidden sm:flex items-center gap-2 text-sm text-app-text-muted">
        <span>Rows per page</span>
        <div className="relative" ref={rowsDropdownRef}>
          <button
            onClick={() => setShowRowsDropdown(!showRowsDropdown)}
            className="flex items-center gap-1 bg-app-surface px-3 py-1.5 rounded-2xl text-app-text font-medium"
          >
            {rowsPerPage} Row
            <ChevronUp
              size={16}
              className={`transition-transform duration-200 shrink-0 ${showRowsDropdown ? "rotate-180" : ""}`}
            />
          </button>

          {showRowsDropdown && (
            <div className="absolute right-0 bottom-full mb-2 w-24 bg-[#000F1F] border border-app-hover rounded-xl shadow-xl z-50 overflow-hidden">
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    onRowsPerPageChange(option);
                    setShowRowsDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors border-b border-app-hover last:border-b-0 ${
                    rowsPerPage === option
                      ? "text-app-accent"
                      : "text-app-text hover:bg-app-hover"
                  }`}
                >
                  {option} Row
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

const TableRow = ({
  item,
  activeTab,
  isSelected,
  showCategoryColumn,
  onSelect,
  onViewTeam,
  onOpenActionMenu,
  onOpenDeleteModal,
}) => {
  return (
    <tr
      className={`border-b border-app-hover transition-colors group ${isSelected ? "bg-app-hover" : ""
        }`}
    >
      {/* Checkbox */}
      <td className="px-4">
        <Checkbox checked={isSelected} onChange={onSelect} />
      </td>

      {/* Name */}
      <td className="px-4">
        <div
          className={`flex items-center gap-3 ${activeTab === TAB_TYPES.TEAMS ? "cursor-pointer hover:opacity-80" : ""
            }`}
          onClick={() => {
            if (activeTab === TAB_TYPES.TEAMS) {
              onViewTeam();
            }
          }}
        >
          <img
            src={item.crest || item.image || teamBadgePlaceholder}
            alt={item.name}
            className="w-5 h-5 rounded-full object-cover"
          />
          <span className="text-clubbie-white font-medium text-sm">
            {item.name}{item.isDeleted && <span className="text-red-400 ml-1">(deleted)</span>}
          </span>
        </div>
      </td>

      {/* Secondary Column - Coach for Teams, Email for Referees/Admins */}
      <td className="hidden sm:table-cell px-4 text-sm text-clubbie-white">
        {activeTab === TAB_TYPES.TEAMS ? item.coach : item.email}
      </td>

      {/* Category - Only for Teams tab with multiple categories */}
      {showCategoryColumn && (
        <td className="px-4 text-sm text-clubbie-white">
          {item.category || "—"}
        </td>
      )}

      {/* Status */}
      <td className="p-4">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusStyle(
            item.status
          )}`}
        >
          {STATUS_LABELS[item.status] || item.status || "Active"}
        </span>
      </td>

      {/* Actions - MoreVertical menu for all tabs (matching Figma) */}
      <td className="px-4 relative">
        <div className="flex justify-end">
          <button
            className="action-menu-trigger text-clubbie-white hover:bg-app-hover p-1.5 rounded-2xl transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onOpenActionMenu(e, item);
            }}
          >
            <MoreHorizontal size={22} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default ParticipantsTable;
